import { bindActionCreators, createAction, createAsyncThunk } from '@reduxjs/toolkit';

import {
  billingAddressFromUserAddress,
  calculateCompatibilityWarning,
  deconstructName,
  truncateBillingAddressFields,
} from './utils/addressUtils';
import { ERROR_CODE, type ErrorCode } from '../js/common/constants';
import { pageView, sendEvent } from '../js/common/analytics';
import { type RootState } from '../redux/store';
import * as api from '../js/common/api';
import buildReviewRequest from './utils/buildReviewRequest';
import type SingleCheckoutTypes from './singleCheckoutTypes';
import buildAnalyticsData from './utils/buildAnalyticsData';

export const extendIssuedCoupon =
  createAsyncThunk('singleCheckout/coupon/extend', async (couponId: number) => api.extendIssuedCoupon(couponId));

export const fetchAttachments =
  createAsyncThunk('singleCheckout/attachments/fetch', async (cartId: number) => api.getAttachmentsByCart(cartId));

export const fetchCheckoutData = createAsyncThunk<
  SingleCheckoutTypes.ReviewApiResponse,
  never,
  { rejectValue: SingleCheckoutTypes.ReviewApiResponseError }
>('singleCheckout/data/fetch',
  async (_, { getState, rejectWithValue }) => {
    try {
      return (await api.getReviewInfoV5(buildReviewRequest(getState() as RootState))).data;
    } catch (err) {
      const error = err as ApiError<SingleCheckoutTypes.ReviewApiResponseError>;
      const errorData = error.response?.data;

      if (errorData?.errorCodes) {
        return rejectWithValue(errorData);
      }

      throw err;
    }
  });

export const removeAddress = createAsyncThunk<
  void,
  Partial<SingleCheckoutTypes.SavedShippingAddress>
>('singleCheckout/shippingAddress/remove', async (address) => {
  if (!address.id) {
    return;
  }

  await api.removeAddress(address.id);
});

export const removeAttachment = createAsyncThunk('singleCheckout/attachment/remove',
  async (fileId: string) => api.removeFileAttachment(fileId));

export const removeSavedPayment =
  createAsyncThunk('singleCheckout/savedPayment/remove', async (savedPaymentId: SingleCheckoutTypes.SavedPaymentId) => api.deleteSavedPayment(savedPaymentId));

export const placeOrder = createAsyncThunk<
  SingleCheckoutTypes.PlaceOrderApiResponse,
  never,
  { rejectValue: SingleCheckoutTypes.ReviewApiResponseError }
>('singleCheckout/order/submit', async (_, { getState, rejectWithValue }) => {
  try {
    return (await api.createOrder(buildReviewRequest(getState() as RootState))).data;
  } catch (err) {
    const error = err as ApiError<SingleCheckoutTypes.ReviewApiResponseError>;
    const errorData = error.response?.data;

    if (errorData?.errorCodes) {
      return rejectWithValue(errorData);
    }

    throw err;
  }
});

export const saveAddress = createAsyncThunk<
  SingleCheckoutTypes.SavedShippingAddress,
  Partial<SingleCheckoutTypes.SavedShippingAddress>
>('singleCheckout/shippingAddress/save', async (address, { getState }) => {
  const {
    restrictions,
  } = (getState() as RootState).singleCheckout;

  const payload = {
    ...address,
    name: address.firstName && address.lastName
      ? `${address.firstName?.trim() || ''} ${address.lastName?.trim() || ''}`.trim()
      : address.name,
  };

  const saved = (await (address.id
    ? api.updateAddress(payload as SingleCheckoutTypes.SavedShippingAddress)
    : api.createAddress(payload))
  ).data;
  const compatibilityWarning = calculateCompatibilityWarning(saved, restrictions);
  return {
    id: saved.addressId,
    compatible: !compatibilityWarning,
    compatibilityWarning,
    paymentVerified: address.id === saved.addressId && address.paymentVerified,
    ...deconstructName(saved.name),
    ...saved,
  };
});

export const selectSavedPayment = createAction<SingleCheckoutTypes.SavedPaymentId>('singleCheckout/savedPayment/select');

const sendAnalyticsEvent: SendEvent = (eventName, eventData = {}) => (_, getState: () => RootState) => {
  sendEvent(eventName, {
    ...buildAnalyticsData(getState()),
    ...eventData,
  });
};

const sendPageView = (eventData: Record<string, any> = {}) => (_, getState: () => RootState) => {
  pageView({
    ...buildAnalyticsData(getState()),
    ...eventData,
  });
};

export const setBraintreeToken = createAction<SingleCheckoutTypes.BraintreeToken>('singleCheckout/braintree/token/set');

export const setCartSetKey = createAction<string>('singleCheckout/cartSetKey/set');

export const setCreditCardDetails = createAsyncThunk<
  SingleCheckoutTypes.TokenizedPaymentString, {
    billingAddress?: SingleCheckoutTypes.UserEnteredAddress;
    id?: SingleCheckoutTypes.SavedPaymentId;
    primary?: boolean;
    rawCardDetails: SingleCheckoutTypes.RawCreditCardDetails;
    save?: boolean;
    useShippingAddress: boolean;
  }, {
    rejectValue: ErrorCode;
  }
>('singleCheckout/creditCardDetails/set',
  async (ccInfo, { getState, rejectWithValue }) => {
    const {
      billingAddress: rawAddress,
      rawCardDetails,
      useShippingAddress,
    } = ccInfo;

    const {
      checkoutData: {
        paymentOptions: {
          CREDIT_CARD: {
            configuration,
          },
        },
      },
      selections: {
        shippingAddress,
      },
    } = (getState() as RootState).singleCheckout;

    let billingAddress: SingleCheckoutTypes.BillingAddress =
      useShippingAddress
        ? billingAddressFromUserAddress(shippingAddress as SingleCheckoutTypes.UserEnteredAddress)
        : rawAddress as SingleCheckoutTypes.BillingAddress;

    billingAddress = truncateBillingAddressFields(billingAddress);

    try {
      const result = (await api.tokenizeCCPayment(
        billingAddress,
        rawCardDetails,
        configuration,
      )).data;

      return result;
    } catch (err) {
      const error = err as ApiError<SingleCheckoutTypes.ReviewApiResponseError>;

      return rejectWithValue(error.response?.data?.errorCodes?.[0] ?? ERROR_CODE.UNKNOWN_ERROR);
    }
  });

export const setActiveCoupon = createAction<{ couponId?: number; prizeId?: number }>('singleCheckout/activeCoupon/set');

export const setCouponFromCart = createAction<boolean>('singleCheckout/coupon/fromCart/set');

export const setCouponId = createAction<number>('singleCheckout/coupon/set');

export const setPrizeId = createAction<number | undefined>('singleCheckout/prize/set');

export const setFFLUploaded = createAction<boolean>('singleCheckout/ffl/uploaded/set');

export const setPaymentAsDefault = createAction<boolean>('singleCheckout/payment/primary/set');

export const setPaymentManagerEditing = createAction<boolean>('singleCheckout/paymentManagerEditing/set');

export const setShippingAddress = createAction<SingleCheckoutTypes.ShippingAddress>('singleCheckout/shippingAddress/set');

export const setShippingMethod = createAction<{ methodId: number; shipmentKey: string; }>('singleCheckout/shippingMethod/set');

export const setShippingReady = createAction<boolean>('singleCheckout/shipping/ready/set');

export const uploadAttachment = createAsyncThunk('singleCheckout/attachment/upload',
  async ({ cartId, file }: { cartId: number; file: File }) => api.uploadFile(cartId, file));

export const validateAddress = createAsyncThunk<
  SingleCheckoutTypes.AddressValidationResponse,
  SingleCheckoutTypes.Address
>('singleCheckout/shippingAddress/validate',
  async (address) => (await api.validateAddress(address)).data);

const singleCheckoutDispatch = (dispatch) => bindActionCreators({
  extendIssuedCoupon,
  fetchAttachments,
  fetchCheckoutData,
  placeOrder,
  removeAddress,
  removeAttachment,
  removeSavedPayment,
  saveAddress,
  selectSavedPayment,
  sendAnalyticsEvent,
  sendPageView,
  setActiveCoupon,
  setBraintreeToken,
  setCartSetKey,
  setCouponFromCart,
  setCouponId,
  setPrizeId,
  setCreditCardDetails,
  setFFLUploaded,
  setPaymentAsDefault,
  setPaymentManagerEditing,
  setShippingAddress,
  setShippingMethod,
  setShippingReady,
  uploadAttachment,
  validateAddress,
}, dispatch);

export default singleCheckoutDispatch;
